import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { HaiIcon } from '@/components/icons';
import type { AdAccountSummary, DashboardSummary } from '@/types';

import { accountNotEnabled } from '../homeAccountStatus';

/**
 * HomeNeedsAction — 首頁「需要你處理」大卡列(R292.17;對齊 D3 `首頁.html`)
 * -----------------------------------------------------------------------------
 * D3 有五張:可能被退件 / 建議調整 / 未檢查 / 尚未上傳廣告 / 帳戶授權異常。
 *
 * ⚠️ **只畫拿得到真數字的卡**(owner 2026-08-30:「首頁就先做已經有的」):
 *   - 尚未上傳廣告 → `dashboard.unimportedAssetCount` ✅
 *   - **廣告帳戶尚未啟用** → 帳戶清單自己算(R298;fallback `dashboard.accountNotEnabledCount`,
 *     BE `41cd785` / v22-18)✅ ⚠️ **與「帳戶授權異常」分兩張卡**:一個要去 Google Ads
 *     啟用、一個要重新授權,混成一張使用者會去按沒用的那個(BE 明寫同一條理由)。
 *     未啟用帳戶的 `status` 仍是 `ACTIVE` → 不會被下面那條重複計到。
 *   - 帳戶授權異常 → **帳戶清單自己算**(R292.18;fallback `dashboard.oauthNeedsReauthCount`)
 *     ✅(**只有「已失效」**;D3 那句「1 個 5 天後失效」需要 token 到期時間,
 *     BE 目前沒有 → owner 拍板「有已經失效的就寫,沒有就不用」)
 *   - 可能被退件 / 建議調整 / 未檢查 → **R292.20 接上了**(BE `dev-update-v21-3`
 *     的 `check_fail_count` / `check_warn_count` / `check_unchecked_count`)。
 *     ⚠️ 那批**尚未併 BE main = beta 沒有** → 缺欄時 mapper 給 `null`,
 *     **整張卡不畫**;不畫「—」也不編 0,那會讓使用者以為「檢查過而且沒問題」。
 *     ⚠️ 三數相加 ≠ `library_total` 是正常的(pass/unk/err 不入計數)。
 *
 * ⚠️ 計數為 0 的**卡**不畫:「需要你處理」列出的是待辦,0 件不是待辦。
 *
 * ## R292.25 — 這一區**永遠會畫一張卡**(四態)
 *
 * owner:「這邊可以先有一個卡叫做載入確認中,都沒有就是沒有(有一張卡『恭喜你暫時
 * 沒有要處理』)。這樣可能一開始才會對齊。」
 *
 * 理由是版面:首頁左右兩欄要以**這一區**為平行線。整區回 `null` 時左欄第一個東西
 * 變成「近期專案」→ 右欄就對齊到近期專案,而不是需要你處理。
 *
 * 四態(**每一態都只講我們真的知道的事**):
 *   1. `loading` → 「確認中…」
 *   2. `dashboard === null`(讀取失敗)→ 「暫時讀不到」。🔴 **不可以說「沒有待處理」**
 *      —— 我們根本沒讀到,那句話會變成一個沒有根據的保證。
 *   3. 有待辦 → 卡片列(原行為不變)
 *   4. 全部為 0 → 「目前沒有需要處理的項目」。⚠️ 若**檢查三計數缺欄**(舊 BE,
 *      `dev-update-v21-3` 未併 main = beta 沒有),那句話涵蓋不到檢查狀態 →
 *      補一行說明,不要讓它被讀成「檢查過而且沒問題」(R292.17 紅線的原意)。
 */
export function HomeNeedsAction({
  dashboard,
  loading,
  accounts,
}: {
  dashboard: DashboardSummary | null;
  loading: boolean;
  /**
   * R292.18 — 帳戶授權異常**優先用帳戶清單自己算**(`status !== 'ACTIVE'`)。
   * ⚠️ 原本只看 `dashboard.oauthNeedsReauthCount`,而那個聚合只數
   * `status=TOKEN_EXPIRED` 的**廣告帳戶**;帳戶清單是同一頁已經載好的真資料,
   * 兩者不一致時以看得見的那份為準(owner:「這應該會有資料?」)。
   * 清單還沒載到(null)→ 退回 dashboard 的數字。
   */
  accounts: AdAccountSummary[] | null;
}) {
  const { t } = useTranslation('home');
  const [hover, setHover] = useState<string | null>(null);

  // ── 態 1 / 2:整區仍要佔位(左右兩欄靠這一區對齊)────────────────────────
  if (loading) {
    return (
      <NoticeCard
        title={t('needs.title')}
        text={t('needs.loading')}
        testId="home-needs-loading"
      />
    );
  }
  if (!dashboard) {
    // 🔴 讀不到 ≠ 沒有待辦 —— 這裡**不可以**改成「恭喜你暫時沒有要處理」
    return (
      <NoticeCard
        title={t('needs.title')}
        text={t('needs.unavailable')}
        testId="home-needs-unavailable"
      />
    );
  }
  // `status` 是封閉聯集 `'ACTIVE' | 'TOKEN_EXPIRED'`,所以 `!== 'ACTIVE'` 恰好等於
  // 「授權已失效」。⚠️ 用 `Array.isArray` 而非 `!== null`:清單還沒載到是 `null`,
  // 但**呼叫端漏傳**是 `undefined` —— 後者若照樣走 `.filter` 會整頁白畫面。
  const oauthBad = Array.isArray(accounts)
    ? accounts.filter((a) => a.status !== 'ACTIVE').length
    : dashboard.oauthNeedsReauthCount;
  /**
   * R298 — 在 Google 端未啟用的帳戶。同 `oauthBad` 的優先序:**帳戶清單自己算**
   * (同一頁已經載好的真資料),清單還沒載到才退回 dashboard 的計數。
   * ⚠️ 退路是 `number | null`:BE 那批未併 main 時是 `null` = 不知道 → 不畫(不是 0)。
   */
  const notEnabled = Array.isArray(accounts)
    ? accounts.filter(accountNotEnabled).length
    : dashboard.accountNotEnabledCount;

  const cards: Array<{ key: string; n: number; unit: string; title: string; hint: string; to: string; tone: string }> = [];
  /**
   * R292.20 — 三張檢查卡。`null` = 這個 BE 沒有那一欄(不是 0)→ 不畫;
   * `0` = 真的沒有待辦 → 也不畫(這一區列的是待辦)。兩種都不畫,但理由不同,
   * 所以判準寫成 `> 0` 而不是 `!= null`(null 走不到 `> 0`,順帶擋掉)。
   */
  const checkCards: Array<{ key: string; n: number | null; title: string; hint: string; tone: string }> = [
    {
      key: 'checkFail',
      n: dashboard.checkFailCount,
      title: t('needs.checkFail.title'),
      hint: t('needs.checkFail.hint'),
      tone: 'var(--rose-700)',
    },
    {
      key: 'checkWarn',
      n: dashboard.checkWarnCount,
      title: t('needs.checkWarn.title'),
      hint: t('needs.checkWarn.hint'),
      tone: 'var(--amber-700)',
    },
    {
      key: 'checkUnchecked',
      n: dashboard.checkUncheckedCount,
      title: t('needs.checkUnchecked.title'),
      hint: t('needs.checkUnchecked.hint'),
      tone: 'var(--ink-300)',
    },
  ];
  for (const c of checkCards) {
    if (c.n === null || c.n <= 0) continue;
    cards.push({
      key: c.key,
      n: c.n,
      unit: t('needs.unitItems'),
      title: c.title,
      hint: c.hint,
      to: '/assets',
      tone: c.tone,
    });
  }

  if (dashboard.unimportedAssetCount > 0) {
    cards.push({
      key: 'unimported',
      n: dashboard.unimportedAssetCount,
      unit: t('needs.unitItems'),
      title: t('needs.unimported.title'),
      hint: t('needs.unimported.hint'),
      to: '/assets',
      // D3 `.tcard.teal`(CIS 沒有綠色;正向 / 可前進 = teal)
      tone: 'var(--concept-fg)',
    });
  }
  if (notEnabled !== null && notEnabled > 0) {
    cards.push({
      key: 'accountNotEnabled',
      n: notEnabled,
      unit: t('needs.unitAccounts'),
      title: t('needs.accountNotEnabled.title'),
      hint: t('needs.accountNotEnabled.hint'),
      to: '/ads/accounts',
      tone: 'var(--rose-700)',
    });
  }
  if (oauthBad > 0) {
    cards.push({
      key: 'oauth',
      n: oauthBad,
      unit: t('needs.unitAccounts'),
      title: t('needs.oauth.title'),
      hint: t('needs.oauth.hint'),
      to: '/ads/accounts',
      tone: 'var(--rose-700)',
    });
  }
  if (cards.length === 0) {
    // 態 4:真的沒有待辦。檢查三計數缺欄時補一行 —— 那三項不在我們的視野裡,
    // 不加註解就等於替 BE 還沒交付的東西背書。
    const blind = dashboard.checkFailCount === null;
    return (
      <NoticeCard
        title={t('needs.title')}
        text={t('needs.allClear')}
        {...(blind ? { hint: t('needs.allClearPartial') } : {})}
        tone="ok"
        testId="home-needs-allclear"
      />
    );
  }

  return (
    <section style={wrapStyle} data-testid="home-needs-action">
      <div style={headStyle}>
        <h2 style={titleStyle}>{t('needs.title')}</h2>
        <span style={subStyle}>{t('needs.scope')}</span>
      </div>
      <div style={gridStyle}>
        {cards.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            style={cardStyle(hover === c.key)}
            onMouseEnter={() => setHover(c.key)}
            onMouseLeave={() => setHover((h) => (h === c.key ? null : h))}
            onFocus={() => setHover(c.key)}
            onBlur={() => setHover((h) => (h === c.key ? null : h))}
            data-testid={`home-needs-${c.key}`}
          >
            <span style={numRowStyle}>
              <b style={{ ...numStyle, color: c.tone }}>{c.n}</b>
              <span style={unitStyle}>{c.unit}</span>
            </span>
            <span style={cardTitleStyle}>{c.title}</span>
            <span style={cardHintStyle}>{c.hint}</span>
            {/* R292.18(owner:「滑鼠移動到那卡片上面會有輕微變化…並且有前往處理」)—
                hover/focus 才出現;`visibility` 而不是條件渲染,卡片高度才不會跳。
                ⚠️ 也綁 focus:鍵盤使用者看不到 hover。 */}
            <span style={goStyle(hover === c.key)} data-testid={`home-needs-go-${c.key}`}>
              {/* D3 `.tc-go` = 「前往處理」+ IC.pgNext(不用 unicode ›) */}
              {t('needs.go')}
              <HaiIcon name="pgNext" size={12} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/**
 * 佔位卡(態 1 / 2 / 4 共用)。**存在本身就是功能** —— 左右兩欄靠這一區對齊,
 * 所以就算沒有待辦也要佔一張卡的高度(見檔頭 R292.25)。
 */
function NoticeCard({
  title,
  text,
  hint,
  tone = 'neutral',
  testId,
}: {
  title: string;
  text: string;
  hint?: string;
  tone?: 'neutral' | 'ok';
  testId: string;
}) {
  return (
    <section style={wrapStyle} data-testid="home-needs-action">
      <div style={headStyle}>
        <h2 style={titleStyle}>{title}</h2>
      </div>
      <div style={noticeCardStyle} data-testid={testId}>
        {/* 「沒問題」= teal(CIS 沒有綠色);D3 `.todo-clear .ok` = teal 淡底圓 + 勾(取代 emoji) */}
        <span style={tone === 'ok' ? { ...noticeTextStyle, ...okTextStyle } : noticeTextStyle}>
          {tone === 'ok' && (
            <span aria-hidden style={okBadgeStyle} data-testid="home-needs-ok-badge">
              <HaiIcon name="ck" />
            </span>
          )}
          {text}
        </span>
        {hint && <span style={noticeHintStyle}>{hint}</span>}
      </div>
    </section>
  );
}

/**
 * R332 — **刻意沒有 margin**:區塊間距由 `HomePage` 左欄的 flex `gap` 給。
 * 原本這裡帶 `marginBottom: 24`,那是「這一區排在近期專案上面」時代的縫;R332 把兩塊
 * 對調之後它會變成頁面底部一條多餘的空白,而兩塊之間反而貼在一起。
 */
const wrapStyle: CSSProperties = {};
const noticeCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '22px 20px',
  borderRadius: 'var(--r-lg)',
  border: '1px solid var(--hairline)',
  background: 'var(--bg-card)',
};
const noticeTextStyle: CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--ink-300)' };
const okTextStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
/** D3 `.todo-clear .ok`:26px teal-50 圓 + teal-700 勾。 */
const okBadgeStyle: CSSProperties = {
  width: 26,
  height: 26,
  flex: '0 0 26px',
  borderRadius: 9999,
  background: 'var(--concept-soft)',
  color: 'var(--concept-fg)',
  display: 'grid',
  placeItems: 'center',
};
const noticeHintStyle: CSSProperties = { fontSize: 11.5, lineHeight: 1.6, color: 'var(--ink-500)' };
const headStyle: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 };
const titleStyle: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-strong)' };
const subStyle: CSSProperties = { fontSize: 12, color: 'var(--ink-500)' };
const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 14,
};
const cardStyle = (hover: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '18px 20px 20px',
  borderRadius: 'var(--r-lg)',
  border: `1px solid ${hover ? 'var(--tide-600)' : 'var(--hairline)'}`,
  background: hover ? 'color-mix(in oklab, var(--tide-400) 8%, var(--bg-card))' : 'var(--bg-card)',
  textDecoration: 'none',
  transition: 'background .12s, border-color .12s',
});

/** hover/focus 才顯的「前往處理 >」(D3 `.tc-go`);用 visibility 保住高度(不讓卡片跳動)。 */
const goStyle = (on: boolean): CSSProperties => ({
  marginTop: 4,
  fontSize: 11.5,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  visibility: on ? 'visible' : 'hidden',
});
const numRowStyle: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 6 };
// D3 `.tc-n`:數字走 sans + tabular-nums(CIS:mono 只給 ID / 帳戶號 / 網域 / 代碼)
const numStyle: CSSProperties = { fontSize: 30, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' };
const unitStyle: CSSProperties = { fontSize: 12, color: 'var(--ink-500)' };
const cardTitleStyle: CSSProperties = { fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', marginTop: 4 };
const cardHintStyle: CSSProperties = { fontSize: 11.5, lineHeight: 1.6, color: 'var(--ink-500)' };
